import {
    ChangeDetectionStrategy,
    Component,
    computed,
    inject,
    input,
    linkedSignal,
    signal,
    viewChild,
} from "@angular/core";
import { S25ItemI } from "../../pojo/S25ItemI";
import { SearchCriteriaType } from "../../pojo/SearchCriteriaI";
import { S25IconButtonComponent } from "../../standalone/s25.icon.button.component";
import { S25Util } from "../../util/s25-util";
import { SpaceService } from "../../services/space.service";
import { ResourceMicroService } from "../../services/resource.micro.service";
import { Item } from "../../pojo/Item";
import { EventMicroService } from "../../services/event.micro.service";
import { OrganizationMicroService } from "../../services/organization.micro.service";
import { S25EditableModule } from "../s25-editable/s25.editable.module";
import { S25LoadingInlineStaticComponent } from "../s25-loading/s25.loading.inline.static.component";
import { S25MultiselectSearchCriteriaComponent } from "../s25-multiselect/multiselects/s25.multiselect.search.criteria.component";
import { EventMicroI } from "../s25-event/EventMicroI";
import { StateStore } from "../../services/state/state.store";
import { S25EventDetailsInlineEditService } from "../object-details/event/s25.event.details.inline.edit.service";
import { S25MultiselectModule } from "../s25-multiselect/s25-multiselect.module";

type PropType = S25ItemI & { quantity?: number };

@Component({
    selector: "s25-ng-editable-property",
    template: `
        @if (isLoading()) {
            <s25-ng-loading-inline-static></s25-ng-loading-inline-static>
        }
        @for (property of editableProperties(); track property.itemId) {
            <div class="property-wrapper">
                @if (canEdit()) {
                    <s25-ng-icon-button
                        [type]="'close'"
                        [ariaLabel]="'Remove property'"
                        (clicked)="$event.stopPropagation(); updateProps({ removed: [property] })"
                    />
                }
                <span class="ng-binding">{{ property.itemName }}</span>
                @if (property.quantity) {
                    @let saveQtyCallback = saveQuantity.bind(this, property);
                    <span class="num-wrapper">
                        <s25-ng-editable-number
                            [val]="property.quantity"
                            [hasCommit]="true"
                            [readOnly]="!canEdit()"
                            [onCommit]="saveQtyCallback"
                            [min]="1"
                            [max]="99999999"
                        ></s25-ng-editable-number>
                    </span>
                }
            </div>
        }
        @if (!canEdit() && !editableProperties()?.length) {
            {{ noneText() }}
        }
        @if (canEdit()) {
            <s25-ng-multiselect-search-criteria
                [type]="propType()!"
                [popoverOnBody]="true"
                [honorMatching]="true"
                [useSecurity]="true"
                [modelBean]="{ hasSelectAll: true }"
                [value]="editableProperties()!"
                (onDone)="updateSelections()"
                (close)="updateSelections()"
            ></s25-ng-multiselect-search-criteria>
        }
        @if (errorMsg()) {
            <p class="ngRed">{{ errorMsg() }}</p>
        }
    `,
    host: {
        "[class.features]": "propPayloadType() === 'features'",
        "[class.connect-25]": "isConnect25()",
    },
    styles: `
        :host {
            display: block;

            s25-loading-inline {
                flex-basis: 100%;
            }

            .property-wrapper {
                display: flex;
                gap: 5px;

                .num-wrapper {
                    margin-left: auto;
                }

                & > span {
                    position: relative;
                    top: 2px;
                }
            }

            &:not(.features) {
                display: flex;
                flex-wrap: wrap;
                gap: 1.5em;

                s25-ng-multiselect-search-criteria {
                    flex-basis: 100%;
                }
            }
        }

        .ngRed {
            font-size: 0.9rem;
            width: fit-content;
            margin: 5px auto;
        }

        s25-ng-loading-inline-static {
            flex-basis: 100%;
            width: fit-content;
            margin: 0 auto;
        }

        :host.connect-25 {
            gap: 6px;
        }

        :host.connect-25 .property-wrapper,
        :host.connect-25 ::ng-deep .aw-button.aw-button--outline.multiSelectButton {
            gap: 0;
            font-size: 11px !important;
            padding: 1px 8px 1px 0;
            border-radius: 8px !important;
            background: var(--color--surface-2-bg) !important;
            color: var(--color--text-muted) !important;
        }

        :host-context(body.nm-party--on).connect-25 .property-wrapper {
            border: 1px solid var(--color--border);
        }

        :host.connect-25 ::ng-deep .aw-button.aw-button--outline.multiSelectButton {
            border-color: unset !important;
        }
        :host.connect-25 ::ng-deep .property-wrapper s25-ng-icon-button .c-iconButton {
            color: var(--color--text-muted);
        }

        :host.connect-25 ::ng-deep .property-wrapper s25-ng-icon-button .c-iconButton:hover {
            color: var(--color--text-strong);
        }

        :host.connect-25 ::ng-deep .aw-button.aw-button--outline.multiSelectButton {
            background: transparent;
            border: 0.5px dashed var(--color--border);
            color: var(--color--text-muted);
            padding-left: 8px;
        }
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [S25LoadingInlineStaticComponent, S25IconButtonComponent, S25EditableModule, S25MultiselectModule],
})
export class S25EditablePropertyComponent {
    itemId = input.required<number>();
    itemTypeId = input.required<Item.Ids>();
    canEdit = input<boolean>();
    properties = input.required<PropType[]>();
    propType = input.required<SearchCriteriaType["type"]>();
    propPayloadType = input.required<"categories" | "features">();
    noneLabel = input<boolean>();

    isLoading = signal<boolean>(false);

    editableProperties = linkedSignal<PropType[]>(() => {
        const props = this.properties();
        if (!props) return [];
        return this.normalize(props);
    });

    noneText = computed(() => this.noneLabel() ?? "none");
    propIdKey = computed(() => (this.propPayloadType() === "categories" ? "categoryId" : "featureId"));
    errorMsg = signal<string>("");

    multiSelect = viewChild<S25MultiselectSearchCriteriaComponent>(S25MultiselectSearchCriteriaComponent);

    serviceMethods: Partial<Record<Item.Ids, Function>> = {
        [Item.Ids.Event]: EventMicroService.microPutEventDetail,
        [Item.Ids.Organization]: OrganizationMicroService.saveOrganization,
        [Item.Ids.Location]: SpaceService.microUpdate,
        [Item.Ids.Resource]: ResourceMicroService.saveResource,
    };

    readonly stateStore = inject(StateStore);
    readonly isConnect25 = computed(() => this.stateStore.isStudentEngagementState());

    private readonly inlineEditService = inject(S25EventDetailsInlineEditService, { optional: true });

    normalize(arr: S25ItemI[]): S25ItemI[] {
        const idProp = this.propPayloadType() === "categories" ? "category_id" : "feature_id";
        const nameProp = this.propPayloadType() === "categories" ? "category_name" : "feature_name";
        return arr.map((item) => ({
            ...item,
            itemId: item.itemId ?? item[idProp],
            itemName: item.itemName ?? item[nameProp],
        }));
    }

    async saveQuantity(prop: PropType, quantity: number) {
        if (!prop && !quantity) return;
        this.isLoading.set(true);
        const payload = {
            [<string>this.propPayloadType()]: [{ [this.propIdKey()]: prop.itemId, quantity: quantity }],
        };
        const serviceMethod = this.serviceMethods[this.itemTypeId()!];
        const savePromise = () => serviceMethod!({ id: this.itemId(), ...payload });
        const [_, error] = await S25Util.Maybe(
            this.itemTypeId() === Item.Ids.Event && this.inlineEditService
                ? this.inlineEditService.runInlineEditScenarios(this.itemId(), savePromise)
                : savePromise(),
        );
        this.isLoading.set(false);
        if (error) {
            this.errorMsg.set(S25Util.errorText(error, ""));
            return;
        }
        this.errorMsg.set("");
    }

    updateSelections() {
        if (!this.multiSelect()?.modelBean) return;
        const { addedItems, removedItems, selectedItems } = this.multiSelect()!.modelBean;

        const existingItemIds = new Set(this.editableProperties().map((property) => property.itemId));
        const added = addedItems?.filter((item) => !existingItemIds.has(item.itemId));
        const removed = removedItems?.filter((item) => existingItemIds.has(item.itemId));

        this.multiSelect()!.modelBean.addedItems = [];
        this.multiSelect()!.modelBean.removedItems = [];
        return this.updateProps({ added, removed, selectedItems });
    }

    async updateProps({
        added,
        removed,
        selectedItems,
    }: {
        added?: S25ItemI[] | undefined;
        removed?: S25ItemI[] | undefined;
        selectedItems?: S25ItemI[] | undefined;
    }) {
        if (!added?.length && !removed?.length) return;

        this.isLoading.set(true);
        const hasQuantity = this.propPayloadType() === "features" && !!added?.length;
        const isEventType = this.itemTypeId() === Item.Ids.Event;

        const payload = this.getPayload({ added, removed, hasQuantity });
        const serviceMethod = this.serviceMethods[this.itemTypeId()!];
        const savePromise = () =>
            isEventType ? serviceMethod!({ items: [payload] }, this.itemId()) : serviceMethod!(payload);
        const [_, error] = await S25Util.Maybe(
            isEventType && this.inlineEditService
                ? this.inlineEditService.runInlineEditScenarios(this.itemId(), savePromise)
                : savePromise(),
        );
        this.isLoading.set(false);

        if (error) {
            return this.errorMsg.set(S25Util.errorText(error, ""));
        }

        this.onUpdate({ added, removed, selectedItems, hasQuantity });
    }

    onUpdate({
        added,
        removed,
        selectedItems,
        hasQuantity,
    }: {
        added: S25ItemI[] | undefined;
        removed: S25ItemI[] | undefined;
        selectedItems: S25ItemI[] | undefined;
        hasQuantity: boolean;
    }) {
        this.errorMsg.set("");
        if (selectedItems && selectedItems.length) {
            // selected from multiselect
            this.editableProperties.set(
                selectedItems.sort(S25Util.shallowSort("sort_order", true)).map((item) => {
                    if (hasQuantity && added?.some((added) => added.itemId === item.itemId)) {
                        item.quantity = item.quantity ?? 1; // preserve quantity
                        return { ...item, quantity: item.quantity };
                    } else return item;
                }),
            );
        } else if (removed && removed[0]) {
            // single removed item
            const updatedItems = this.editableProperties()
                .filter((prop) => prop.itemId !== removed[0].itemId)
                .sort(S25Util.shallowSort("sort_order", true));
            this.editableProperties.set(updatedItems);
            const multiSelect = this.multiSelect();
            multiSelect?.modelBean?.removeSelectedItem?.(removed[0]);
        }
    }

    getPayload({
        added,
        removed,
        hasQuantity,
    }: {
        added: S25ItemI[] | undefined;
        removed: S25ItemI[] | undefined;
        hasQuantity: boolean;
    }) {
        const data: EventMicroI = {};
        if (added?.length) {
            data[<string>this.propPayloadType()] = added.map((item) => ({
                [this.propIdKey()]: item.itemId,
                ...(hasQuantity && { quantity: 1 }),
            }));
        }
        if (removed?.length) {
            data.remove = {
                [<string>this.propPayloadType()]: removed.map((item) => ({ [this.propIdKey()]: item.itemId })),
            };
        }
        return { id: this.itemId(), ...data };
    }
}
